import React, { useEffect, useState, useCallback } from "react";
import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { useAtom } from "jotai";
import { addressPlaceDetailsAtom } from "./analysisAtoms";
import { Stack, Autocomplete, TextField } from "@mui/material";

export const AddressAutocomplete = () => {
    const [, setAddressPlaceDetails] = useAtom(addressPlaceDetailsAtom);
    const map = useMap();
    const places = useMapsLibrary("places");

    const [sessionToken, setSessionToken] = useState(null);
    const [autocompleteService, setAutocompleteService] = useState(null);
    const [placesService, setPlacesService] = useState(null);
    const [predictionResults, setPredictionResults] = useState([]);
    const [inputValue, setInputValue] = useState("");

    useEffect(() => {
        if (!places || !map) return;

        setAutocompleteService(new places.AutocompleteService());
        setPlacesService(new places.PlacesService(map));
        setSessionToken(new places.AutocompleteSessionToken());

        return () => setAutocompleteService(null);
    }, [map, places]);

    const fetchPredictions = useCallback(
        async (inputValue) => {
            if (!autocompleteService || !inputValue) {
                setPredictionResults([]);
                return;
            }

            const request = { input: inputValue, sessionToken };
            const response =
                await autocompleteService.getPlacePredictions(request);

            setPredictionResults([...response.predictions]);
        },
        [autocompleteService, sessionToken]
    );

    const handleSuggestionClick = useCallback(
        (placeId) => {
            if (!places) return;

            const detailRequestOptions = {
                placeId,
                fields: ["geometry", "name", "formatted_address"],
                sessionToken
            };

            const detailsRequestCallback = (placeDetails) => {
                setAddressPlaceDetails(placeDetails);
                setPredictionResults([]);
                setInputValue(placeDetails?.formatted_address ?? "");
                setSessionToken(new places.AutocompleteSessionToken());
            };

            placesService?.getDetails(
                detailRequestOptions,
                detailsRequestCallback
            );
        },
        [places, placesService, sessionToken, setAddressPlaceDetails]
    );

    const handleInputChange = (event, value) => {
        setInputValue(value);
        if (inputReady(value)) fetchPredictions(value);
        if (!value) setAddressPlaceDetails(null);
    };
    function inputReady(inp) {
        if (inp && inp.length > 2) {
            return true;
        }
        return false;
    }

    const handleOptionSelect = (event, option) => {
        if (option && option.place_id) {
            handleSuggestionClick(option.place_id);
        } else if (!option) {
            setInputValue("");
            setPredictionResults([]);
        }
    };

    return (
        <Stack sx={{ width: "400px", padding: "6px", borderRadius: "4px" }}>
            <Autocomplete
                freeSolo
                autoComplete
                options={predictionResults}
                getOptionLabel={(option) => option.description || ""}
                inputValue={inputValue}
                onInputChange={handleInputChange}
                onChange={handleOptionSelect}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        size="small"
                        label="Address look-up"
                        variant="outlined"
                        sx={{
                            backgroundColor: "white",
                            borderRadius: "4px",
                            "&:hover": { backgroundColor: "white" },
                            "& .MuiFilledInput-root": {
                                "&:hover": { backgroundColor: "white" },
                                backgroundColor: "white",
                                "&.Mui-focused": {
                                    backgroundColor: "white"
                                }
                            }
                        }}
                    />
                )}
                sx={{
                    "& .MuiInputBase-input": {
                        fontSize: "14px"
                    },
                    "& .MuiAutocomplete-option": {
                        fontSize: "14px"
                    }
                }}
            />
        </Stack>
    );
};
